// Permanent horizontal Lightning multiplier reel — presentation only; tier from server.
import type { I18n } from '@aerofruits/i18n';
import {
  formatLightningBonusTenths,
  formatMultiplierTenths,
  LIGHTNING_TIER_ORDER,
  SECTOR_LABEL,
  type LightningTier,
} from '../game/lightning-types.js';

export interface LightningReelParams {
  tier: LightningTier;
  baseMultiplierTenths: number;
  lightningMultiplierTenths: number;
  totalMultiplierTenths: number;
  payoutMinor: number;
  currency: string;
  reducedMotion: boolean;
  i18n: I18n;
  formatMoney: (minor: number, currency: string) => string;
}

export interface LightningReelBounds {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface LightningReelSectionRects {
  host: DOMRect;
  title: DOMRect;
  viewport: DOMRect;
}

const TIER_CLASS: Record<LightningTier, string> = {
  BOOST_0_5: 'lightning-card--tier-low',
  BOOST_1_5: 'lightning-card--tier-low',
  BOOST_2_5: 'lightning-card--tier-mid',
  BOOST_4_5: 'lightning-card--tier-mid',
  BOOST_9_5: 'lightning-card--tier-high',
  BOOST_19_5: 'lightning-card--tier-rare',
};

const REPEAT_COPIES = 4;
const ANIM_MS = 2400;
const ANIM_MS_REDUCED = 350;

function tierIndex(tier: LightningTier): number {
  const i = LIGHTNING_TIER_ORDER.indexOf(tier);
  return i >= 0 ? i : 0;
}

export class LightningReel {
  private readonly root: HTMLElement;
  private readonly viewport: HTMLElement;
  private readonly track: HTMLElement;
  private readonly live: HTMLElement;
  private readonly getReducedMotion: () => boolean;
  private readonly i18n: I18n;

  private animating = false;
  private selectedTier: LightningTier | null = null;
  private resizeObserver: ResizeObserver | undefined;
  private cardStep = 0;
  private pendingAnim: LightningReelParams | null = null;

  constructor(
    root: HTMLElement,
    opts: {
      i18n: I18n;
      formatMoney: (minor: number, currency: string) => string;
      reducedMotion: () => boolean;
    },
  ) {
    this.root = root;
    this.i18n = opts.i18n;
    this.getReducedMotion = opts.reducedMotion;

    root.classList.add('lightning-reel');
    root.setAttribute('role', 'group');
    root.setAttribute('aria-label', opts.i18n.t('lightning.reel.label'));
    root.innerHTML = `
      <div class="lightning-reel__title">${opts.i18n.t('lightning.bonus.title')}</div>
      <div class="lightning-reel__viewport" id="lightning-reel-viewport">
        <div class="lightning-reel__pointer" aria-hidden="true"></div>
        <div class="lightning-reel__track" id="lightning-reel-track" data-mode="idle"></div>
      </div>
      <div class="lightning-reel__live sr-only" id="lightning-reel-live" aria-live="polite" aria-atomic="true"></div>`;

    this.viewport = root.querySelector('#lightning-reel-viewport')!;
    this.track = root.querySelector('#lightning-reel-track')!;
    this.live = root.querySelector('#lightning-reel-live')!;

    this.buildIdleTrack(null);

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.track.dataset.mode === 'spin') {
          this.measureSpin();
        } else if (!this.animating) {
          this.buildIdleTrack(this.selectedTier);
        }
      });
      this.resizeObserver.observe(this.viewport);
    }
  }

  destroy(): void {
    this.resizeObserver?.disconnect();
  }

  setBounds(bounds: LightningReelBounds): void {
    this.root.style.left = `${bounds.left}px`;
    this.root.style.top = `${bounds.top}px`;
    this.root.style.width = `${bounds.width}px`;
    this.root.style.height = `${bounds.height}px`;
    requestAnimationFrame(() => {
      if (!this.animating && this.track.dataset.mode !== 'spin') {
        this.buildIdleTrack(this.selectedTier);
      }
    });
  }

  /** Measured host box — used by layout regression tests. */
  getLayoutRect(): { width: number; height: number } {
    const r = this.root.getBoundingClientRect();
    const width = r.width || parseFloat(this.root.style.width) || this.root.offsetWidth;
    const height = r.height || parseFloat(this.root.style.height) || this.root.offsetHeight;
    return { width, height };
  }

  /** Section bounding boxes for overlap regression tests. */
  getSectionRects(): LightningReelSectionRects {
    const title = this.root.querySelector('.lightning-reel__title')!;
    return {
      host: this.root.getBoundingClientRect(),
      title: title.getBoundingClientRect(),
      viewport: this.viewport.getBoundingClientRect(),
    };
  }

  /** Count of multiplier cards intersecting the reel viewport (idle: all six). */
  countVisibleCards(): number {
    if (this.track.dataset.mode === 'idle') {
      return this.track.querySelectorAll('.lightning-card').length;
    }
    const vp = this.viewport.getBoundingClientRect();
    const vpWidth = vp.width || this.viewport.clientWidth || parseFloat(this.viewport.style.width);
    if (vpWidth <= 0) return this.track.querySelectorAll('.lightning-card').length;
    let count = 0;
    for (const card of this.track.querySelectorAll('.lightning-card')) {
      const cr = card.getBoundingClientRect();
      if (cr.width <= 0 || cr.height <= 0) continue;
      if (cr.right > vp.left + 1 && cr.left < vp.right - 1) count++;
    }
    return count;
  }

  applyTranslations(): void {
    const t = this.i18n.t.bind(this.i18n);
    const title = this.root.querySelector('.lightning-reel__title');
    if (title) title.textContent = t('lightning.bonus.title');
    this.root.setAttribute('aria-label', t('lightning.reel.label'));
  }

  /** Clears highlight when a new roll begins. */
  prepareRoll(): void {
    this.pendingAnim = null;
    this.selectedTier = null;
    this.setCardState('idle');
    this.live.textContent = '';
    this.root.classList.remove('lightning-reel--no-match');
    this.buildIdleTrack(null);
  }

  setIdle(): void {
    this.root.classList.remove('lightning-reel--no-match', 'lightning-reel--animating');
    this.buildIdleTrack(this.selectedTier);
  }

  showNoMatch(): void {
    if (this.animating) return;
    this.root.classList.remove('lightning-reel--animating');
    this.setCardState('disabled');
    this.root.classList.add('lightning-reel--no-match');
    const t = this.i18n.t.bind(this.i18n);
    this.live.textContent = t('lightning.result.notActivated');
  }

  async animateWin(params: LightningReelParams): Promise<void> {
    this.pendingAnim = params;
    this.root.classList.remove('lightning-reel--no-match');
    this.buildSpinTrack();
    this.measureSpin();
    if (this.cardStep <= 0) {
      await new Promise((r) => requestAnimationFrame(r));
      this.measureSpin();
    }
    if (this.cardStep <= 0) return;

    this.animating = true;
    this.root.classList.add('lightning-reel--animating');
    this.setCardState('moving');

    const idx = tierIndex(params.tier);
    const reduced = params.reducedMotion || this.getReducedMotion();
    const laps = reduced ? 1 : 3;
    const target = this.offsetForCardIndex(laps * LIGHTNING_TIER_ORDER.length + idx);

    this.track.style.transition = reduced
      ? `transform ${ANIM_MS_REDUCED}ms ease-out`
      : `transform ${ANIM_MS}ms cubic-bezier(0.12, 0.85, 0.15, 1)`;

    const start = this.offsetForCardIndex(0);
    this.track.style.transform = `translateX(${start}px)`;
    void this.track.offsetWidth;

    await new Promise<void>((resolve) => {
      const onEnd = (): void => {
        this.track.removeEventListener('transitionend', onEnd);
        resolve();
      };
      this.track.addEventListener('transitionend', onEnd);
      requestAnimationFrame(() => {
        this.track.style.transform = `translateX(${target}px)`;
      });
      window.setTimeout(resolve, reduced ? ANIM_MS_REDUCED + 50 : ANIM_MS + 80);
    });

    this.selectedTier = params.tier;
    this.animating = false;
    this.pendingAnim = null;
    this.root.classList.remove('lightning-reel--animating');
    this.buildIdleTrack(params.tier);

    const t = params.i18n.t.bind(params.i18n);
    this.live.textContent = `${t('lightning.result.lightningBonus', {
      bonus: formatLightningBonusTenths(params.lightningMultiplierTenths),
    })}. ${t('lightning.result.totalWin', {
      total: formatMultiplierTenths(params.totalMultiplierTenths),
      win: params.formatMoney(params.payoutMinor, params.currency),
    })}`;
  }

  cancel(): void {
    this.animating = false;
    this.pendingAnim = null;
    this.track.style.transition = 'none';
    this.root.classList.remove('lightning-reel--animating');
    this.buildIdleTrack(this.selectedTier);
  }

  private createCard(tier: LightningTier): HTMLElement {
    const card = document.createElement('div');
    card.className = `lightning-card ${TIER_CLASS[tier]}`;
    card.dataset.tier = tier;
    card.textContent = SECTOR_LABEL[tier];
    return card;
  }

  /** Idle: all six tiers visible in the viewport without horizontal scroll. */
  private buildIdleTrack(highlight: LightningTier | null): void {
    this.track.dataset.mode = 'idle';
    this.track.style.transition = 'none';
    this.track.style.transform = 'none';
    this.track.replaceChildren();
    for (const tier of LIGHTNING_TIER_ORDER) {
      const card = this.createCard(tier);
      if (highlight === tier) card.classList.add('lightning-card--selected');
      this.track.appendChild(card);
    }
  }

  /** Spin: repeated strip for the win animation. */
  private buildSpinTrack(): void {
    this.track.dataset.mode = 'spin';
    this.track.style.transition = 'none';
    this.track.replaceChildren();
    const total = LIGHTNING_TIER_ORDER.length * REPEAT_COPIES;
    for (let i = 0; i < total; i++) {
      const tier = LIGHTNING_TIER_ORDER[i % LIGHTNING_TIER_ORDER.length]!;
      this.track.appendChild(this.createCard(tier));
    }
  }

  private measureSpin(): void {
    const card = this.track.querySelector<HTMLElement>('.lightning-card');
    if (!card) return;
    const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap || '0') || 0;
    const w = card.getBoundingClientRect().width || card.offsetWidth;
    this.cardStep = w > 0 ? w + gap : 64;
  }

  private viewportCenterX(): number {
    return this.viewport.clientWidth / 2;
  }

  private offsetForCardIndex(cardIndex: number): number {
    const center = this.viewportCenterX();
    const cardCenter = cardIndex * this.cardStep + this.cardStep / 2;
    return center - cardCenter;
  }

  private setCardState(state: 'idle' | 'moving' | 'disabled'): void {
    this.track.dataset.state = state;
    this.track.querySelectorAll('.lightning-card').forEach((el) => {
      el.classList.remove('lightning-card--selected', 'lightning-card--previous', 'lightning-card--flash');
      if (state === 'disabled') el.classList.add('lightning-card--disabled');
      else el.classList.remove('lightning-card--disabled');
    });
    if (state === 'idle' && this.selectedTier) {
      this.track.querySelectorAll('.lightning-card').forEach((el) => {
        if (el.getAttribute('data-tier') === this.selectedTier) {
          el.classList.add('lightning-card--selected');
        }
      });
    }
  }
}
